import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { DownloadFile } from "../../../pages/medicalHistory/MedicalHistoryPdf";
import { GetAccessApiToken, getSessionData } from "../../../helpers/Util";
import { CallingApp } from "../../../Constant";
import { fetchInstance } from "../../../helpers/interceptor";


/* ─── Viewport helpers ───────────────────────────────────────
   On mobile, 100vh includes the browser address bar, so a "96vh" modal is
   taller than the visible screen and its header gets cut off. Use dynamic
   viewport units (dvh) where supported, else the real inner height. */
const supportsDvh =
  typeof CSS !== "undefined" && typeof CSS.supports === "function" && CSS.supports("height", "100dvh");
const viewportHeight = (pct) =>
  supportsDvh ? `${pct}dvh` : `${Math.round((window.innerHeight * pct) / 100)}px`;

const isMobileDevice = () =>
  (typeof window !== "undefined" && window.matchMedia?.("(max-width: 767px)").matches) ||
  /Android|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || "");

/* ─── pdf.js (loaded on demand from CDN) ─────────────────────
   Mobile browsers can't show a PDF inside an iframe (they show an "Open"
   button instead), so on mobile we render the pages ourselves with pdf.js. */
const PDFJS_VERSION = "3.11.174";
const PDFJS_BASE = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${PDFJS_VERSION}`;
let pdfjsPromise = null;
const loadPdfJs = () => {
  if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
  if (pdfjsPromise) return pdfjsPromise;
  pdfjsPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${PDFJS_BASE}/pdf.min.js`;
    script.async = true;
    script.onload = () => {
      const lib = window.pdfjsLib;
      if (!lib) { pdfjsPromise = null; reject(new Error("pdf.js not available")); return; }
      lib.GlobalWorkerOptions.workerSrc = `${PDFJS_BASE}/pdf.worker.min.js`;
      resolve(lib);
    };
    script.onerror = () => { pdfjsPromise = null; reject(new Error("Failed to load pdf.js")); };
    document.head.appendChild(script);
  });
  return pdfjsPromise;
};

/* ─── Inline styles (scoped, no global CSS needed) ─────────── */
const S = {
  backdrop: {
    position: "fixed",
    inset: 0,
    zIndex: 9999,
    background: "rgba(15, 12, 10, 0.68)",
    backdropFilter: "blur(4px)",
    WebkitBackdropFilter: "blur(4px)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    boxSizing: "border-box",
  },
  shell: {
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 24px 64px rgba(0,0,0,0.28)",
    border: "1.5px solid #e5ddd3",
    width: "100%",
    maxWidth: 900,
    height: "96vh",
    maxHeight: "96vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    animation: "pv-slideUp 0.26s cubic-bezier(0.22,0.9,0.34,1) both",
  },
  header: {
    background: "linear-gradient(135deg, #176b62 0%, #2a9d8f 100%)",
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 10,
    flexShrink: 0,
  },
  headerLeft: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
    flex: 1,
  },
  headerTitle: {
    margin: 0,
    fontWeight: 700,
    fontSize: 15,
    color: "#fff",
    letterSpacing: "0.02em",
    lineHeight: 1.3,
  },
  headerMeta: {
    fontSize: 12.5,
    color: "rgba(255,255,255,0.78)",
    marginTop: 2,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexShrink: 0,
  },
  toggle: (isOn) => ({
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    userSelect: "none",
    whiteSpace: "nowrap",
  }),
  toggleTrack: (isOn) => ({
    width: 46,
    height: 24,
    borderRadius: 20,
    background: isOn ? "#3cb377" : "rgba(255,255,255,0.3)",
    position: "relative",
    transition: "background 0.25s",
    flexShrink: 0,
    border: "1.5px solid rgba(255,255,255,0.4)",
  }),
  toggleThumb: (isOn) => ({
    width: 18,
    height: 18,
    borderRadius: "50%",
    background: "#fff",
    position: "absolute",
    top: 2,
    left: isOn ? 24 : 2,
    transition: "left 0.25s",
    boxShadow: "0 1px 4px rgba(0,0,0,0.2)",
  }),
  toggleLabel: {
    fontSize: 12.5,
    fontWeight: 600,
    color: "rgba(255,255,255,0.92)",
  },
  closeBtn: {
    width: 32,
    height: 32,
    borderRadius: 7,
    border: "1.5px solid rgba(255,255,255,0.3)",
    background: "rgba(255,255,255,0.12)",
    color: "#fff",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 15,
    flexShrink: 0,
    transition: "background 0.15s",
    outline: "none",
  },
  body: {
    flex: 1,
    overflow: "hidden",
    position: "relative",
    minHeight: 0,
  },
  loadingOverlay: {
    position: "absolute",
    inset: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#fdfbf8",
    zIndex: 2,
  },
  spinner: {
    width: 36,
    height: 36,
    border: "3px solid #e2d5c3",
    borderTopColor: "#2a9d8f",
    borderRadius: "50%",
    animation: "pv-spin 0.75s linear infinite",
  },
  pagesWrap: {
    position: "absolute",
    inset: 0,
    overflowY: "auto",
    overflowX: "hidden",
    WebkitOverflowScrolling: "touch",
    background: "#e9e4dc",
    padding: 8,
    boxSizing: "border-box",
  },
  iframe: {
    display: "block",
    width: "100%",
    height: "100%",
    border: "none",
  },
  footer: {
    background: "#fdfbf8",
    borderTop: "1.5px solid #e5ddd3",
    padding: "8px 16px",
    display: "flex",
    justifyContent: "center",
    gap: 10,
    flexShrink: 0,
  },
  footerCloseBtn: {
    padding: "6px 22px",
    fontSize: 12.5,
    fontWeight: 700,
    background: "#fff",
    color: "#176b62",
    border: "1.5px solid #e5ddd3",
    borderRadius: 7,
    cursor: "pointer",
    letterSpacing: "0.04em",
    transition: "background 0.15s",
    outline: "none",
  },
  fullscreenBtn: {
    padding: "6px 22px",
    fontSize: 12.5,
    fontWeight: 700,
    background: "linear-gradient(135deg, #176b62, #2a9d8f)",
    color: "#fff",
    border: "none",
    borderRadius: 7,
    cursor: "pointer",
    letterSpacing: "0.04em",
    transition: "opacity 0.15s, transform 0.15s",
    outline: "none",
  },
};

/* ─── Keyframe injection (once) ───────────────────────────── */
const injectKeyframes = (() => {
  let done = false;
  return () => {
    if (done) return;
    done = true;
    const style = document.createElement("style");
    style.textContent = `
      @keyframes pv-slideUp {
        from { opacity: 0; transform: translateY(28px) scale(0.98); }
        to   { opacity: 1; transform: translateY(0)    scale(1);    }
      }
      @keyframes pv-spin {
        to { transform: rotate(360deg); }
      }
    `;
    document.head.appendChild(style);
  };
})();


const PdfView = ({ path, row, isRealDataVisible, OnHide, GetProcedureCode }) => {
  injectKeyframes();

  const [loading,    setLoading]    = useState(true);
  const [objectURL,  setObjectURL]  = useState("");
  const [currentPath, setCurrentPath] = useState(path);
  const [isOn,       setIsOn]       = useState(path?.includes("_verbose"));
  const [templateId, setTemplateId] = useState(false);
  const [pdfBlob,    setPdfBlob]    = useState(null);
  const [isMobile]                  = useState(isMobileDevice);
  const [canvasFailed, setCanvasFailed] = useState(false);
  const pagesRef = useRef(null);

  const closeBtnRef = useRef(null);

  const selectedRow = row;
  const IsVerboseNote = true;

  const tokenData  = getSessionData("tokenData", {});
  const companyId  = tokenData?.companyId || "";
  const companyName = tokenData?.companyName || "";

  /* ── Fetch PDF blob ──────────────────────────────────────── */
  const showPdf = async (filePath) => {
    setLoading(true);
    await DownloadFile(filePath, (blob) => {
      if (blob === 401) return;

      if (blob === 404 || blob === 0) {
        setLoading(false);
        OnHide();
        if (filePath.includes("_verbose.pdf")) {
          setIsOn(false);
          setCurrentPath((p) => p.replace("_verbose.pdf", ".pdf"));
          console.error("The verbose note is unavailable. Please regenerate the appointment.");
          toast.error("Something went wrong.");
        }
        return;
      }

      const url = URL.createObjectURL(blob);
      if (!url) {
        setLoading(false);
        OnHide();
        toast.error("Data not found");
      } else {
        setObjectURL((prev) => {
          if (prev) URL.revokeObjectURL(prev); // clean up old blob URL
          return url;
        });
        setPdfBlob(blob);
        setLoading(false);
      }
    });
  };

  /* ── Fetch verbose-eligible template IDs ─────────────────── */
  const fetchTemplateIds = async () => {
    const TokenInfo = GetAccessApiToken();
    try {
      const res = await fetchInstance(
        `${process.env.REACT_APP_DOCTOR_API_URL}Template/GetVerboseNoteTemplateIds?companyId=${companyId}`,
        {
          headers: {
            Authorization: `Bearer ${TokenInfo.AccessToken}`,
            CallingApp,
          },
        }
      );
      const result = await res.json();
      setTemplateId(result.includes(selectedRow?.NoteTemplateId));
    } catch (_) {}
  };

  useEffect(() => {
    showPdf(currentPath);
    fetchTemplateIds();
    // Cleanup blob URL on unmount
    return () => {
      if (objectURL) URL.revokeObjectURL(objectURL);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Escape key to close ─────────────────────────────────── */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") hidePopup(); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* ── Focus close button on open (a11y) ───────────────────── */
  useEffect(() => {
    if (isRealDataVisible) closeBtnRef.current?.focus();
  }, [isRealDataVisible]);

  /* ── Mobile: render PDF pages to canvases inside the modal ── */
  const useCanvasView = isMobile && !canvasFailed;
  useEffect(() => {
    if (!useCanvasView || !pdfBlob || !pagesRef.current) return;
    let cancelled = false;
    let pdfDoc = null;
    const container = pagesRef.current;

    (async () => {
      try {
        setLoading(true);
        const pdfjsLib = await loadPdfJs();
        const data = new Uint8Array(await pdfBlob.arrayBuffer());
        pdfDoc = await pdfjsLib.getDocument({ data }).promise;
        if (cancelled) return;
        container.innerHTML = "";
        const cssWidth = Math.max(container.clientWidth - 16, 200);
        const dpr = Math.min(window.devicePixelRatio || 1, 3);

        for (let n = 1; n <= pdfDoc.numPages; n++) {
          const page = await pdfDoc.getPage(n);
          if (cancelled) return;
          const base = page.getViewport({ scale: 1 });
          const viewport = page.getViewport({ scale: (cssWidth / base.width) * dpr });
          const canvas = document.createElement("canvas");
          canvas.width = Math.floor(viewport.width);
          canvas.height = Math.floor(viewport.height);
          canvas.style.width = "100%";
          canvas.style.height = "auto";
          canvas.style.display = "block";
          canvas.style.background = "#fff";
          canvas.style.margin = "0 auto 8px";
          canvas.style.boxShadow = "0 1px 4px rgba(0,0,0,0.15)";
          container.appendChild(canvas);
          await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          if (n === 1 && !cancelled) setLoading(false); // show first page as soon as it's ready
        }
        if (!cancelled) setLoading(false);
      } catch (err) {
        console.error("PDF render failed, falling back to iframe:", err);
        if (!cancelled) { setCanvasFailed(true); setLoading(false); }
      }
    })();

    return () => {
      cancelled = true;
      pdfDoc?.destroy?.();
    };
  }, [useCanvasView, pdfBlob]);

  /* ── Verbose toggle ──────────────────────────────────────── */
  const handleToggle = () => {
    const next = isOn
      ? currentPath.replace("_verbose.pdf", ".pdf")
      : currentPath.replace(".pdf", "_verbose.pdf");
    setCurrentPath(next);
    setIsOn((v) => !v);
    showPdf(next);
  };

  /* ── Update filename on close ────────────────────────────── */
  const updateMedicalNotesFileName = async () => {  
    const TokenInfo = GetAccessApiToken();
    const extractPath = (url) => {
      const idx = url.indexOf("path=");
      return idx !== -1 ? url.substring(idx + 5) : "";
    };
    await fetchInstance(
      `${process.env.REACT_APP_DOCTOR_API_URL}GenericAppointment/updateMedicalNotesFileName`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${TokenInfo.AccessToken}`,
          CallingApp,
        },
        body: JSON.stringify({
          User: {
            Physician: { ID: tokenData.doctorId },
            Account:   { ID: companyId },
            Domain:    "greenyourbills.com",
            Specialty: { ID: tokenData.doctorSpecialtyId, Name: tokenData.doctorSpecialtyName, Code: GetProcedureCode(tokenData.doctorSpecialtyId) },
            ID:        tokenData.userId,
            UserName:  tokenData.userName,
          },
          Appointment: {
            Patient: { CaseID: tokenData.caseId },
            ID:      row.ID,
          },
          FilePath:       extractPath(currentPath),
          NoteTemplateId: selectedRow?.NoteTemplateId,
        }),
      }
    ).catch(console.error);
  };

  /* ── Close ───────────────────────────────────────────────── */
  const hidePopup = () => {
    if (IsVerboseNote && templateId) updateMedicalNotesFileName();
    OnHide();
  };

  /* ── Full-screen ─────────────────────────────────────────── */
  const openFullScreen = () => {
    const w = window.open(objectURL, "_blank");
    if (!w) toast.error("Unable to open in a new window.");
    else w.focus();
  };

  /* ── Header info ─────────────────────────────────────────── */


  const showVerboseToggle = IsVerboseNote && templateId;

  if (!isRealDataVisible) return null;

  /* ── Always mount on body to escape any parent stacking context ── */
  const mountNode = document.body;

  /* ── Portal content ──────────────────────────────────────── */
  return createPortal(
    <div
      style={{ ...S.backdrop, ...(isMobile ? { padding: 0, alignItems: "stretch" } : {}) }}
      role="dialog"
      aria-modal="true"
      aria-label="PDF Viewer"
      onClick={(e) => { if (e.target === e.currentTarget) hidePopup(); }}
    >
      <div
        style={{
          ...S.shell,
          ...(isMobile
            ? { height: viewportHeight(100), maxHeight: viewportHeight(100), borderRadius: 0, border: "none" }
            : { height: viewportHeight(96), maxHeight: viewportHeight(96) }),
        }}
      >

        {/* ── Header ── */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <h5 style={S.headerTitle}>Notes</h5>
          </div>

          <div style={S.headerRight}>
            {showVerboseToggle && (
              <div style={S.toggle(isOn)} onClick={handleToggle} role="switch" aria-checked={isOn} tabIndex={0} onKeyDown={(e) => e.key === "Enter" && handleToggle()}>
                <div style={S.toggleTrack(isOn)}>
                  <div style={S.toggleThumb(isOn)} />
                </div>
                <span style={S.toggleLabel}>Verbose note</span>
              </div>
            )}

            <button
              ref={closeBtnRef}
              style={S.closeBtn}
              onClick={hidePopup}
              aria-label="Close PDF viewer"
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.25)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "rgba(255,255,255,0.12)")}
            >
              ✕
            </button>
          </div>
        </div>

        {/* ── Body ── */}
        <div style={{ ...S.body }}>
          {loading && (
            <div style={S.loadingOverlay}>
              <div style={S.spinner} />
            </div>
          )}
          {useCanvasView ? (
            <div ref={pagesRef} style={S.pagesWrap} aria-label="PDF pages" />
          ) : (
            <iframe
              key={objectURL}
              src={objectURL ? `${objectURL}#navpanes=0&view=fitH` : ""}
              style={S.iframe}
              title="PDF Viewer"
            />
          )}
        </div>

        {/* ── Footer ── */}
        <div style={S.footer}>
          <button
            style={S.footerCloseBtn}
            onClick={hidePopup}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f0e9")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}
          >
            Close
          </button>
          {objectURL && !loading && (
            <button
              style={S.fullscreenBtn}
              onClick={openFullScreen}
              onMouseEnter={(e) => { e.currentTarget.style.opacity = "0.88"; e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.opacity = "1";    e.currentTarget.style.transform = "translateY(0)"; }}
            >
              Enter Full Screen
            </button>
          )}
        </div>

      </div>
    </div>,
    mountNode
  );
};

export default PdfView;